import { Component, OnInit, Input, Output, EventEmitter } from '@angular/core';
import { GroupService } from 'src/app/core/service/group.service';
import { Validators, UntypedFormBuilder, UntypedFormGroup, UntypedFormControl } from '@angular/forms';
import { User, Group } from 'src/app/user/model/user';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { GridViewManager } from '../../../core/model/grid-view-manager';
import { sortBy } from 'lodash';
import { UserService } from 'src/app/core/service/user.service';
import { DataStateChangeEvent } from '@progress/kendo-angular-grid';
import { Organization } from 'src/app/core/model/organization';
import { OrganizationService } from 'src/app/core/service/organization.service';
import { AuthenticationService } from 'src/app/core/authentication/authentication.service';
import { GridEventHandler } from 'src/app/core/model/grid-settings';
import { TranslateService } from '@ngx-translate/core';

/**
 * Angular component for channel edit.
 */
@Component({
  selector: 'app-channel-group-edit',
  templateUrl: './channel-group-edit.component.html',
  styleUrls: ['./channel-group-edit.component.scss']
})
export class ChannelGroupEditComponent implements OnInit
{
  /**
   * Is this a new group?
   */
  @Input() public isNew = false;

  @Output() public cancelling: EventEmitter<any> = new EventEmitter();
  @Output() public afterSave: EventEmitter<Group> = new EventEmitter();

  public view: GridViewManager;
  public editFormActive = false;

  /**
   * Data model of the channel group.
   */
  public group: Group = new Group();

  /**
   * Form group for the group.
   */
  public groupForm: any;

  /**
   * Channel staff.
   */
  public staff = [];

  /**
   * Candidate users for channel staff drop-down.
   */
  public staffCandidates = [];

  /**
   */
  public staffList = [];

  /**
   * The newly added staff member.
   */
  public newStaffMember = null;

  /**
   * Filtered list of staffs candidates for drop-down.
   */
  public staffCandidatesFiltered: User[] = [];

  /**
   * Current search term entered from a comboxbox
   */
  public searchTerm: string;

  /**
   * List of available organizations
   */
  public organizations: Organization[];

  /**
   * Filtered list of organizations for drop-down.
   */
  public organizationsFiltered: Organization[] = [];

  /**
   * Coordinator service admin role?
   */
  public isCommunityCoordinatorRole = false;

  /**
   * Program lead role?
   */
  public isProgramLeadRole = false;

  public currentUserOrganization: Organization;

  public count = 0;
  public recordDisabledText = '';
  public recordDeletedText = '';

  /**
   * Current logged in user
   */
  private currentUser: User;

  /**
   * Constructor.
   */
  constructor(
    private formBuilder: UntypedFormBuilder,
    private groupService: GroupService,
    private userService: UserService,
    private translate: TranslateService,
    private messageHandlerService: MessageHandlerService,
    private organizationService: OrganizationService,
    private authenticationService: AuthenticationService)
  {
    this.view = new GridViewManager(99999);
  }

  public get organization(): UntypedFormControl
  {
    return this.groupForm.get('organization');
  }

  @Input() public set model(group: Group)
  {
    this.initModel(group);
  }

  public ngOnInit(): void
  {
    this.currentUser = this.authenticationService.currentUser;
    if (this.currentUser)
    {
      this.userService.getUserById(this.currentUser.id, false).subscribe({
        next: (user) =>
        {
          this.isCommunityCoordinatorRole = user.role === 'ROLE_COMMUNITY_COORDINATOR';
          this.isProgramLeadRole = user.role === 'ROLE_PROGRAM_LEAD';
          this.currentUserOrganization = user.organization;
        }
      });
    }

    this.userService.getCachedUsers().subscribe({
      next: (data) =>
      {
        this.staffList = data;
      }
    });

    // Get Organizations
    this.organizationService.getCachedOrganizations().subscribe({
      next: (data) =>
      {
        this.organizations = data;
        this.filterOrganizations('');
      }
    });

    this.translate.get(['RECORD-DISABLED', 'RECORD-DELETED']).subscribe({
      next: (res) =>
      {
        this.recordDisabledText = res['RECORD-DISABLED'];
        this.recordDeletedText = res['RECORD-DELETED'];
      }
    });
  }

  public formValid(): boolean
  {
    return this.groupForm.valid;
  }

  public formDirty(): boolean
  {
    return this.groupForm.dirty;
  }

  /**
   * Delete user from list.
   *
   *@param user User to delete.
   */
  public deleteStaff(user: User): void
  {
    this.staff = this.staff.filter(u => u.id !== user.id);
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.groupForm.markAsDirty();
    this.count = this.staff.length;
  }

  /**
   * Staff cancel handler
   */
  public staffCancelHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
  }

  /**
   * Staff add handler
   */
  public staffAddHandler(event: GridEventHandler): void
  {
    this.newStaffMember = null;
    this.staffCandidates = sortBy(this.staffList.filter(u => !this.staff.map(m => m.id).includes(u.id)), ['name']);
    this.filterStaffCandidates('');
    event.sender.addRow(new UntypedFormGroup({ editing: new UntypedFormControl(true) }));
  }

  /**
   * Add staff to grid in preparation for save.
   */
  public staffSaveHandler(event: GridEventHandler): void
  {
    event.sender.closeRow(event.rowIndex);
    this.staff.unshift(this.newStaffMember);
    this.staff = sortBy(this.staff, ['name']);
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.count = this.staff.length;
    this.groupForm.markAsDirty();
  }

  /**
   * Filters the channel staffs based on user input.
   */
  public filterStaffCandidates(filter: string): void
  {
    this.searchTerm = filter;
    this.staffCandidatesFiltered = this.staffCandidates
      .filter(user => user.name.toLowerCase().includes(filter.toLowerCase()));
  }

  /**
   * Check if user from dropdown is disabled or enabled.
   *
   * @returns boolean
   */
  public itemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.enabled && !itemArgs.dataItem.inRegistration;
  }

  /**
   * Disable inactive items from drop-down.
   *
   * @param itemArgs Item object
   */
  public organizatonItemDisabled(itemArgs: { dataItem: any; index: number }): boolean
  {
    return !itemArgs.dataItem.active;
  }

  /**
   * Edit form has been closed.
   */
  public onCancel(e: Event): void
  {
    e.preventDefault();
    this.closeEditForm();
  }

  /**
   * Save group.
   */
  public saveGroup(): void
  {
    this.group = this.groupForm.value;
    this.staff.forEach(user =>
    {
      user.department = null;
      user.organization = null;
    });
    this.group.users = this.staff;

    if (this.organization.value === undefined)
    {
      this.group.organization = null;
    }

    this.groupService.persistGroup(this.group, this.isNew, this.group.id).subscribe({
      next: (data) =>
      {
        this.messageHandlerService.displaySuccess('GROUP-SAVED-SUCCESSFULLY');
        this.afterSave.emit(data);
      },
      error: (errorResponse) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.editFormActive = false;
      }
    });
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.view.setState(state);
  }

  /**
   * Filter the organizations based on user input.
   */
  public filterOrganizations(filterValue: string): void
  {
    this.organizationsFiltered = sortBy(this.organizations
      .filter(organization => organization.name.toLowerCase().includes(filterValue.toLowerCase())), ['name']);
  }

  public setRecordStateText(dataItem: any): string
  {
    if (dataItem && dataItem.deleted)
    {
      return this.recordDeletedText;
    }
    else if (dataItem && !dataItem.enabled)
    {
      return this.recordDisabledText;
    }
    else
    {
      return '';
    }
  }

  /**
   * Initialize form model.
   *
   * @param group Group to edit or insert.
   */
  private initModel(group: Group)
  {
    if (group === undefined)
    {
      return;
    }

    // Set form
    this.groupForm = this.formBuilder.group({
      id: this.group.id,
      name: [this.group.name, [Validators.required, Validators.maxLength(255)]],
      description: this.group.description,
      active: this.group.active,
      organization: this.isCommunityCoordinatorRole || this.isProgramLeadRole ? this.currentUserOrganization : this.group.organization
    });

    this.groupForm.reset(group);
    this.group = group;
    this.staff = sortBy(group.users, ['fullName']);
    this.view.state.skip = 0;
    this.view.setData(this.staff);
    this.count = this.staff.length;
    this.editFormActive = true;
  }

  /**
   * Close edit form.
   */
  private closeEditForm(): void
  {
    this.editFormActive = false;
    this.cancelling.emit();
  }
}
